import type {QueryClient} from '@tanstack/react-query'
import EventEmitter from 'eventemitter3'
import {nanoid} from 'nanoid/non-secure'

import {TapApiClient} from '#/lib/api/tap'
import {
  BATCH_INTERVAL,
  INITIAL_RECONNECT_DELAY,
  MAX_RECONNECT_ATTEMPTS,
  MAX_RECONNECT_DELAY,
  MAX_SEEN_URIS,
} from './const'
import type {
  TapChannelEvent,
  TapFeedUpdateEvent,
  TapFeedUpdatesParams,
} from './types'
import {TapFeedUpdatesStatus} from './types'

const LOGGER_CONTEXT = 'TapFeedUpdates'

/**
 * WebSocket client for TAP API real-time feed updates
 * Follows MessagesEventBus pattern for consistency
 */
export class TapFeedUpdatesEventBus {
  private id: string
  private ws: WebSocket | null = null
  private queryClient: QueryClient
  private accessToken: string | undefined

  private emitter = new EventEmitter<{event: [TapFeedUpdateEvent]}>()
  private status: TapFeedUpdatesStatus = TapFeedUpdatesStatus.Initializing

  // Reconnection state
  private reconnectAttempts = 0
  private reconnectTimeout: NodeJS.Timeout | undefined

  // Event batching
  private batchBuffer: TapChannelEvent[] = []
  private batchTimeout: NodeJS.Timeout | undefined

  // Deduplication
  private seenUris = new Set<string>()

  constructor(params: TapFeedUpdatesParams) {
    this.id = nanoid(3)
    this.queryClient = params.queryClient
    this.accessToken = params.accessToken

    console.log(`[${LOGGER_CONTEXT}] Initialized`, {
      id: this.id,
      hasAuth: !!this.accessToken,
    })
  }

  /**
   * Subscribe to events
   */
  on(handler: (event: TapFeedUpdateEvent) => void) {
    this.emitter.on('event', handler)
    return () => {
      this.emitter.off('event', handler)
    }
  }

  /**
   * Handle app going to background
   */
  background() {
    console.log(`[${LOGGER_CONTEXT}] App backgrounded`, {id: this.id})
    if (this.status !== TapFeedUpdatesStatus.Suspended) {
      this.status = TapFeedUpdatesStatus.Backgrounded
      this.disconnect()
    }
  }

  /**
   * Handle app suspension
   */
  suspend() {
    console.log(`[${LOGGER_CONTEXT}] Suspending`, {id: this.id})
    this.status = TapFeedUpdatesStatus.Suspended
    this.disconnect()
    this.clearBatchTimeout()
    this.clearReconnectTimeout()
  }

  /**
   * Handle app resuming or initial start
   */
  resume() {
    console.log(`[${LOGGER_CONTEXT}] Resuming`, {id: this.id})
    if (
      this.status === TapFeedUpdatesStatus.Suspended ||
      this.status === TapFeedUpdatesStatus.Backgrounded ||
      this.status === TapFeedUpdatesStatus.Initializing
    ) {
      this.connect()
    }
  }

  /**
   * Establish WebSocket connection
   */
  private connect() {
    if (this.ws?.readyState === WebSocket.OPEN) {
      console.log(`[${LOGGER_CONTEXT}] Already connected`, {id: this.id})
      return
    }

    try {
      const client = new TapApiClient()
      const wsUrl = client.getWebSocketUrl()

      console.log(`[${LOGGER_CONTEXT}] Connecting to WebSocket`, {
        id: this.id,
        url: wsUrl,
        attempt: this.reconnectAttempts + 1,
      })

      // Don't block app initialization - connect asynchronously
      this.ws = new WebSocket(wsUrl)

      // Set connection timeout to avoid blocking
      const connectionTimeout = setTimeout(() => {
        if (this.ws?.readyState === WebSocket.CONNECTING) {
          console.log(`[${LOGGER_CONTEXT}] Connection timeout`, {id: this.id})
          this.ws?.close()
        }
      }, 5000) // 5 second timeout

      this.ws.onopen = () => {
        clearTimeout(connectionTimeout) // Clear timeout on successful connection
        console.log(`[${LOGGER_CONTEXT}] Connected`, {
          id: this.id,
          url: wsUrl,
        })
        this.status = TapFeedUpdatesStatus.Connected
        this.reconnectAttempts = 0 // Reset on successful connection

        // Send auth message
        if (this.accessToken) {
          this.ws?.send(
            JSON.stringify({
              type: 'auth',
              token: this.accessToken,
            }),
          )
          console.log(`[${LOGGER_CONTEXT}] Sent auth message`, {id: this.id})
        }

        this.emitter.emit('event', {type: 'connect'})
      }

      this.ws.onmessage = event => {
        this.handleMessage(event.data)
      }

      this.ws.onerror = error => {
        console.error(`[${LOGGER_CONTEXT}] WebSocket error`, {
          id: this.id,
          error,
        })
        this.emitter.emit('event', {
          type: 'error',
          error: new Error('WebSocket connection error'),
        })
      }

      this.ws.onclose = event => {
        clearTimeout(connectionTimeout) // Clear timeout
        console.log(`[${LOGGER_CONTEXT}] Disconnected`, {
          id: this.id,
          code: event.code,
          reason: event.reason,
          wasClean: event.wasClean,
        })

        this.emitter.emit('event', {type: 'disconnect'})

        // Attempt reconnection if not suspended or backgrounded
        if (
          this.status !== TapFeedUpdatesStatus.Suspended &&
          this.status !== TapFeedUpdatesStatus.Backgrounded
        ) {
          this.reconnect()
        }
      }
    } catch (error) {
      console.error(`[${LOGGER_CONTEXT}] Failed to create WebSocket`, {
        id: this.id,
        error,
      })
      this.reconnect()
    }
  }

  /**
   * Close WebSocket connection
   */
  private disconnect() {
    if (this.ws) {
      this.ws.close()
      this.ws = null
    }
    this.clearReconnectTimeout()
  }

  /**
   * Reconnect with exponential backoff
   */
  private reconnect() {
    this.reconnectAttempts++

    if (this.reconnectAttempts > MAX_RECONNECT_ATTEMPTS) {
      console.error(`[${LOGGER_CONTEXT}] Max reconnection attempts reached`, {
        id: this.id,
        attempts: this.reconnectAttempts,
      })
      this.status = TapFeedUpdatesStatus.Error
      this.emitter.emit('event', {
        type: 'error',
        error: new Error('Max reconnection attempts reached'),
      })
      return
    }

    const delay = Math.min(
      INITIAL_RECONNECT_DELAY * Math.pow(2, this.reconnectAttempts - 1),
      MAX_RECONNECT_DELAY,
    )

    console.log(`[${LOGGER_CONTEXT}] Reconnecting`, {
      id: this.id,
      attempt: this.reconnectAttempts,
      delayMs: delay,
    })

    this.status = TapFeedUpdatesStatus.Reconnecting
    this.clearReconnectTimeout()
    this.reconnectTimeout = setTimeout(() => {
      this.connect()
    }, delay)
  }

  /**
   * Handle incoming WebSocket message
   */
  private handleMessage(data: string) {
    try {
      const event = JSON.parse(data) as TapChannelEvent

      console.log(`[${LOGGER_CONTEXT}] Message received`, {
        id: this.id,
        collection: event.collection,
        action: event.action,
        uri: event.uri?.slice(-15),
      })

      // Filter and batch events
      if (this.shouldIncludeEvent(event)) {
        this.batchBuffer.push(event)
        this.seenUris.add(event.uri)

        // Manage seen URIs cache size (FIFO)
        if (this.seenUris.size > MAX_SEEN_URIS) {
          const firstUri = Array.from(this.seenUris)[0]
          this.seenUris.delete(firstUri)
        }

        // Schedule batch processing
        this.scheduleBatchProcessing()
      }
    } catch (error) {
      console.error(`[${LOGGER_CONTEXT}] Failed to parse message`, {
        id: this.id,
        error,
        data: data.slice(0, 100),
      })
    }
  }

  /**
   * Check if event should be included
   */
  private shouldIncludeEvent(event: TapChannelEvent): boolean {
    // Only post creation events
    if (
      event.collection !== 'app.bsky.feed.post' ||
      event.action !== 'create'
    ) {
      return false
    }

    // Deduplicate
    if (this.seenUris.has(event.uri)) {
      return false
    }

    return true
  }

  /**
   * Schedule batch processing
   */
  private scheduleBatchProcessing() {
    if (this.batchTimeout) {
      return // Already scheduled
    }

    this.batchTimeout = setTimeout(() => {
      this.processBatch()
    }, BATCH_INTERVAL)
  }

  /**
   * Process batched events and invalidate queries
   */
  private processBatch() {
    this.clearBatchTimeout()

    if (this.batchBuffer.length === 0) {
      return
    }

    const events = [...this.batchBuffer]
    this.batchBuffer = []

    console.log(`[${LOGGER_CONTEXT}] Processing batch`, {
      id: this.id,
      count: events.length,
      uris: events.map(e => e.uri.slice(-15)),
    })

    // Mark queries as stale (but don't trigger immediate refetch)
    // React Query will refetch when:
    // 1. User manually pulls to refresh
    // 2. User navigates away and back to the tab
    // 3. Window regains focus (web)
    this.queryClient.invalidateQueries({
      queryKey: ['tap-video-posts'],
      refetchType: 'none',
    })
    this.queryClient.invalidateQueries({
      queryKey: ['tap-all-posts'],
      refetchType: 'none',
    })

    console.log(`[${LOGGER_CONTEXT}] Marked queries as stale`, {
      id: this.id,
      count: events.length,
      invalidatedKeys: ['tap-video-posts', 'tap-all-posts'],
    })

    // Emit batch event for any listeners
    this.emitter.emit('event', {type: 'batch', events})
  }

  /**
   * Clear batch timeout
   */
  private clearBatchTimeout() {
    if (this.batchTimeout) {
      clearTimeout(this.batchTimeout)
      this.batchTimeout = undefined
    }
  }

  /**
   * Clear reconnect timeout
   */
  private clearReconnectTimeout() {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout)
      this.reconnectTimeout = undefined
    }
  }
}
